import { decodeHtmlEntities, stripHtml, truncateText } from "./html";

/** Search engines show roughly this many characters of a description. */
export const PRODUCT_META_DESCRIPTION_MAX_LENGTH = 155;

type ProductDescriptionFields = {
	MetaDescription?: string | null;
	SubDescription?: string | null;
	Description?: string | null;
};

/** schema.org Product.description: Google reads up to this many characters. */
export const PRODUCT_SCHEMA_DESCRIPTION_MAX_LENGTH = 5000;

/** The first candidate with content, as plain text with entities decoded. */
const firstPlainText = (candidates: (string | null | undefined)[]): string =>
	candidates
		// Strip first so encoded text like `&lt;3&gt;` is not read as a tag;
		// collapse again because decoded `&nbsp;` adds fresh whitespace.
		.map((field) =>
			decodeHtmlEntities(stripHtml(field)).replace(/\s+/g, " ").trim()
		)
		.find(Boolean) || "";

/**
 * Meta description from the first HTML/text candidate that has content,
 * as plain text truncated to search-result length.
 */
export const buildMetaDescription = (
	...candidates: (string | null | undefined)[]
): string => {
	const text = firstPlainText(candidates);
	return text ? truncateText(text, PRODUCT_META_DESCRIPTION_MAX_LENGTH) : "";
};

/**
 * The editor's SEO field in full (Google shortens snippets to the device
 * width itself), else the first fallback with text, cut to search length.
 */
export const buildMetaDescriptionFromSeoField = (
	seoField: string | null | undefined,
	...fallbacks: (string | null | undefined)[]
): string => firstPlainText([seoField]) || buildMetaDescription(...fallbacks);

/**
 * Meta description for a PDP, from the first Norce text field that has
 * content: the SEO field (uncut), then the short description shown next to
 * the price, then the long description.
 *
 * `SubHeader` is left out on purpose — clients use it for campaign copy
 * with an end date, which goes stale in search results.
 */
export const buildProductMetaDescription = (
	product: ProductDescriptionFields | null | undefined
): string =>
	product
		? buildMetaDescriptionFromSeoField(
				product.MetaDescription,
				product.SubDescription,
				product.Description
			)
		: "";

type ProductSchemaFields = ProductDescriptionFields & {
	SubHeader?: string | null;
};

/**
 * schema.org Product.description: the full product text, not the 155-char
 * meta cut — the long description first, then the shorter fields.
 */
export const buildProductSchemaDescription = (
	product: ProductSchemaFields | null | undefined
): string => {
	if (!product) return "";
	const text = firstPlainText([
		product.Description,
		product.SubDescription,
		product.MetaDescription,
		product.SubHeader
	]);
	return truncateText(text, PRODUCT_SCHEMA_DESCRIPTION_MAX_LENGTH);
};
